import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Home = () => {
  // Animation variants
  return (
    <>
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden min-h-[90vh] flex items-center">
        {/* Hero Image Background */}
        <div className="absolute inset-0 z-0">
          <img
            src={`${process.env.PUBLIC_URL}/images/hero2.jpeg`}
            alt="Cinematic Production"
            className="object-cover w-full h-full scale-105 animate-slow-zoom"
          />
          {/* Add a dark overlay for better text visibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>

        <div className="container relative z-10 flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl mx-auto text-center text-white"
          >
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mb-6 text-5xl font-bold md:text-7xl font-display leading-tight"
            >
              Stay <motion.span
                initial={{ color: "#F8F0E9" }}
                animate={{ color: "#FF914D" }}
                transition={{ duration: 1, delay: 0.5 }}
                className="text-[#FF914D] inline-block"
              >
                Crisp
              </motion.span>, Stay Seen.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mb-10 text-xl text-[#FFE5D0]/90 md:text-2xl max-w-2xl mx-auto font-light"
            >
              From bland to branded—we help small businesses serve content that's crisp, creative, and impossible to scroll past.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="flex flex-wrap justify-center gap-6"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/contact" className="btn bg-[#FF914D] text-white hover:bg-[#FFB86B] px-8 py-4 text-lg rounded-full shadow-lg shadow-orange-500/30">
                  Get Started
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/portfolio" className="btn bg-white/10 backdrop-blur-sm text-white border border-white/30 hover:bg-white/20 px-8 py-4 text-lg rounded-full">
                  View Work
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white/70 flex flex-col items-center"
        >
          <span className="text-sm mb-2 tracking-widest uppercase">Scroll</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center p-1"
          >
            <div className="w-1 h-2 bg-white/80 rounded-full"></div>
          </motion.div>
        </motion.div>
      </section>

      {/* Process Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF3E0] to-[#FFE0B2] opacity-50"></div>
        <div className="container relative z-10">
          <div className="text-center mb-16">
            <span className="text-[#FF914D] font-bold tracking-wider uppercase text-sm">Our Process</span>
            <h2 className="mt-2 text-4xl font-bold font-display text-dark">How We Work</h2>
            <p className="max-w-2xl mx-auto mt-4 text-lg text-gray-600">
              Our proven process ensures we deliver content that's crisp, creative, and converts.
            </p>
          </div>

          <div className="relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-[#FF914D]/30 -translate-y-1/2 z-0"></div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
              {[
                {
                  step: "01",
                  title: "Discovery",
                  description: "We deep-dive into your brand, audience, and goals to craft a winning strategy.",
                  icon: "🔍"
                },
                {
                  step: "02",
                  title: "Create",
                  description: "We design scroll-stopping content that matches your unique voice.",
                  icon: "🎨"
                },
                {
                  step: "03",
                  title: "Publish",
                  description: "We schedule and share at the right time to maximize engagement.",
                  icon: "🚀"
                },
                {
                  step: "04",
                  title: "Optimize",
                  description: "We track, learn, and refine for continuous growth.",
                  icon: "📈"
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="relative z-10 flex flex-col items-center group"
                >
                  <div className="flex items-center justify-center w-20 h-20 mb-6 text-white rounded-2xl bg-white shadow-xl group-hover:-translate-y-2 transition-transform duration-300 border border-orange-100">
                    <span className="text-3xl">{step.icon}</span>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-orange-50 w-full h-full hover:shadow-md transition-shadow">
                    <div className="text-[#FF914D] font-bold text-sm mb-2">STEP {step.step}</div>
                    <h3 className="mb-3 text-xl font-bold text-dark">{step.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between mb-16">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <span className="text-[#FF914D] font-bold tracking-wider uppercase text-sm">Why Choose Us</span>
              <h2 className="mt-2 text-4xl font-bold font-display text-dark">Our Core Values</h2>
            </div>
            <div className="md:w-1/2 md:pl-12 border-l-4 border-[#FF914D]">
              <p className="text-xl text-gray-600 italic">
                "We don't just post; we partner. Your growth is our growth."
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: "Creativity & Innovation",
                description: "We don't recycle content — we bake fresh ideas daily. From bold campaigns to out-of-the-box strategies.",
                color: "bg-blue-50 text-blue-600"
              },
              {
                title: "Authenticity",
                description: "No gimmicks. No fake fluff. Just real content, honest conversations, and strategies that build trust.",
                color: "bg-green-50 text-green-600"
              },
              {
                title: "Excellence",
                description: "From brainstorm to publish, we don't just plan — we deliver results that crunch. Precision in every pixel.",
                color: "bg-purple-50 text-purple-600"
              },
              {
                title: "Growth Mindset",
                description: "The digital world never stops evolving, and neither do we. We constantly learn, adapt, and optimize.",
                color: "bg-orange-50 text-orange-600"
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`p-8 rounded-2xl ${value.color.split(' ')[0]} hover:shadow-lg transition-shadow duration-300`}
              >
                <h3 className={`mb-4 text-xl font-bold ${value.color.split(' ')[1]}`}>{value.title}</h3>
                <p className="text-gray-700 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="container">
          <h2 className="mb-12 text-4xl font-bold text-center font-display">Frequently Asked Questions</h2>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                question: "When will I see results from social media marketing?",
                answer: "You'll spot the first sparks within a month — more likes, comments, and reach. Big wins like steady leads and conversions? Those usually roll in around 3–6 months. Social media is like good bread — it rises best with time. 🍞✨"
              },
              {
                question: "Which platforms are best for my business?",
                answer: "There's no one-size-fits-all. Instagram = visuals, LinkedIn = B2B & credibility, Facebook = communities, and more. We'll help you mix the right recipe so your brand shows up exactly where your audience hangs out."
              },
              {
                question: "How do you track social media campaign success?",
                answer: "We don't just count likes. We track engagement, reach, clicks, followers, and ROI. Then we break it down into monthly reports you'll actually understand (no jargon, promise). Clear insights, smart tweaks, better results."
              },
              {
                question: "Why invest in social media marketing?",
                answer: "Because your customers are already scrolling 👀. Social media builds trust, visibility, and traffic while boosting sales. We craft growth-focused strategies so you don't just post pretty pictures — you actually grow."
              },
              {
                question: "Can you manage our content and posting?",
                answer: "Oh, 100%. We handle the whole deal — ideas, content creation, scheduling, analytics, and optimization. Basically, we're your in-house social team, minus the payroll headache. You focus on business; we'll bring the buzz. 🚀"
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                <details className="group">
                  <summary className="flex justify-between items-center font-medium cursor-pointer list-none p-6 text-lg text-dark hover:text-[#FF914D] transition-colors">
                    <span>{faq.question}</span>
                    <span className="transition group-open:rotate-180">
                      <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24"><path d="M6 9l6 6 6-6"></path></svg>
                    </span>
                  </summary>
                  <div className="text-gray-600 px-6 pb-6 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/contact" className="text-[#FF914D] font-semibold hover:underline text-lg">
              Have more questions? Contact us &rarr;
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;